// What LAMP knows (ADR-0021): the business's Suggestions, and a Memory panel per Counterparty with its
// behaviour calculated from the Ledgers, its Notes with their source, and the Relationship Notes a person
// wrote. Nothing here is hidden model memory: every line can be read, and forgotten. The Application API
// checks every action again and records each change in the Audit Log.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  RELATIONSHIP_NOTE_CATEGORIES,
  type Api,
  type BusinessMemory,
  type CounterpartyMemory,
  type MemoryNote,
  type RelationshipNoteCategory,
  type Suggestion,
} from "../api.js";
import { locale } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { CATALOGUES, useLanguage, useWords, type Words } from "../i18n/words.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

/** How each Channel a note came by is said, as a catalogue key. */
const CHANNELS = {
  chat: "memoryViaChat",
  whatsapp: "memoryViaWhatsapp",
  email: "memoryViaEmail",
  phone: "memoryViaPhone",
  in_person: "memoryInPerson",
  cockpit: "memoryViaCockpit",
} as const satisfies Record<string, keyof Words>;
const isChannel = (channel: string): channel is keyof typeof CHANNELS => Object.hasOwn(CHANNELS, channel);

/** The words of each kind of Relationship Note, as a catalogue key. */
const CATEGORIES = {
  family_event: "memoryCategoryFamilyEvent",
  health: "memoryCategoryHealth",
  festival_greeting: "memoryCategoryFestivalGreeting",
  greeting_preference: "memoryCategoryGreetingPreference",
  courtesy: "memoryCategoryCourtesy",
} as const satisfies Record<RelationshipNoteCategory, keyof Words>;

const day = (iso: string, language: Language) =>
  new Intl.DateTimeFormat(locale(language), { timeZone: "Asia/Kolkata", dateStyle: "medium" }).format(new Date(iso));

/** Who saved a note, when and where, in the business's words. */
export function sourceOf(note: MemoryNote, language: Language = "en"): string {
  const w = CATALOGUES[language];
  const { by, agent, at, channel } = note.source;
  const where = isChannel(channel) ? w[CHANNELS[channel]] : w.memoryVia(channel);
  return w.memorySource(by, Boolean(agent), day(at, language), where);
}

function Forget({ what, busy, onForget }: { what: string; busy: boolean; onForget: () => Promise<boolean> }) {
  const w = useWords();
  const [asking, setAsking] = useState(false);
  if (!asking)
    return (
      <button
        type="button"
        className="btn btn-small"
        aria-label={w.memoryForgetLabel(what)}
        onClick={() => setAsking(true)}
      >
        {w.memoryForget}
      </button>
    );
  return (
    <span role="group" aria-label={w.memoryForgetAsk(what)}>
      {w.memoryForgetWarning}{" "}
      <button
        type="button"
        className="btn btn-small btn-go"
        disabled={busy}
        onClick={() => void onForget().then(() => setAsking(false))}
      >
        {w.memoryForgetIt}
      </button>{" "}
      <button type="button" className="btn btn-small" onClick={() => setAsking(false)}>
        {w.memoryKeepIt}
      </button>
    </span>
  );
}

function NoteForm({ busy, onAdd }: { busy: boolean; onAdd: (text: string) => Promise<boolean> }) {
  const w = useWords();
  const [text, setText] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onAdd(text).then((saved) => saved && setText(""));
  };
  return (
    <form className="card" aria-label={w.memoryAddNote} onSubmit={submit}>
      <label className="field" htmlFor="note-text">
        {w.memoryNoteHint}
      </label>
      <input id="note-text" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} />
      <button type="submit" className="btn btn-go" disabled={busy || text.trim() === ""}>
        {w.memorySaveNote}
      </button>
    </form>
  );
}

function RelationshipNoteForm({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (input: { contact: string; category: RelationshipNoteCategory; text: string }) => Promise<boolean>;
}) {
  const w = useWords();
  const [contact, setContact] = useState("");
  const [category, setCategory] = useState<RelationshipNoteCategory>("family_event");
  const [text, setText] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onAdd({ contact, category, text }).then((saved) => {
      if (!saved) return;
      setContact("");
      setText("");
    });
  };
  return (
    <form className="card" aria-label={w.memoryAddRelationshipNote} onSubmit={submit}>
      <p className="muted">{w.memoryRelationshipRules}</p>
      <label className="field" htmlFor="relationship-contact">
        {w.memoryContact}
      </label>
      <input id="relationship-contact" value={contact} maxLength={100} onChange={(e) => setContact(e.target.value)} />
      <label className="field" htmlFor="relationship-category">
        {w.memoryKind}
      </label>
      <select
        id="relationship-category"
        value={category}
        onChange={(e) => setCategory(e.target.value as RelationshipNoteCategory)}
      >
        {(Object.keys(RELATIONSHIP_NOTE_CATEGORIES) as RelationshipNoteCategory[]).map((c) => (
          <option key={c} value={c}>
            {w[CATEGORIES[c]]}
          </option>
        ))}
      </select>
      <label className="field" htmlFor="relationship-text">
        {w.memoryNote}
      </label>
      <input
        id="relationship-text"
        value={text}
        maxLength={category === "health" ? 140 : 500}
        onChange={(e) => setText(e.target.value)}
      />
      <button type="submit" className="btn btn-go" disabled={busy || contact.trim() === "" || text.trim() === ""}>
        {w.memorySaveRelationshipNote}
      </button>
    </form>
  );
}

function Behaviour({ memory }: { memory: CounterpartyMemory }) {
  const w = useWords();
  const b = memory.behaviour;
  if (!b || b.paid === 0) return <p>{w.memoryNoBehaviour}</p>;
  return (
    <p>
      {w.memoryBehaviour(b.onTimePercent ?? 0, b.paid, b.averageDaysLate, b.overdue)}{" "}
      <span className="muted">{w.memoryFromLedgers}</span>
    </p>
  );
}

/** The Memory panel of one Counterparty. */
export function CounterpartyMemoryPanel({ api, counterparty }: { api: Api; counterparty: string }) {
  const w = useWords();
  const { language } = useLanguage();
  const [memory, setMemory] = useState<CounterpartyMemory | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMemory(undefined);
    api.counterpartyMemory(counterparty).then(setMemory, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api, counterparty]);

  const attempt = async (work: () => Promise<CounterpartyMemory>, done: string): Promise<boolean> => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      setMemory(await work());
      setNotice(done);
      return true;
    } catch (error) {
      setProblem(problemOf(error, w));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!memory) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.memoryLoading}</p>;
  const name = memory.counterparty.name;
  const forget = (note: MemoryNote) => attempt(() => api.forgetNote(counterparty, note.id), w.memoryForgotten);
  return (
    <section className="section" aria-labelledby="memory-counterparty">
      <h2 id="memory-counterparty">{w.memoryKnowsAbout(name)}</h2>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <h3>{w.memoryBehaviourHeading}</h3>
      <Behaviour memory={memory} />
      <h3>{w.memoryNotes}</h3>
      {memory.notes.length === 0 ? <p>{w.memoryNoNotes}</p> : null}
      <ul className="plain-list" aria-label={w.memoryNotes}>
        {memory.notes.map((n) => (
          <li key={n.id}>
            {n.text} <span className="muted">— {sourceOf(n, language)}</span>{" "}
            <Forget what={`"${n.text}"`} busy={busy} onForget={() => forget(n)} />
          </li>
        ))}
      </ul>
      <NoteForm
        busy={busy}
        onAdd={(text) => attempt(() => api.addNote(counterparty, { text, channel: "cockpit" }), w.memoryNoteSaved)}
      />
      {memory.relationshipNotes ? (
        <>
          <h3>{w.memoryRelationshipNotes}</h3>
          {memory.relationshipNotes.length === 0 ? <p>{w.memoryNoRelationshipNotes}</p> : null}
          <ul className="plain-list" aria-label={w.memoryRelationshipNotes}>
            {memory.relationshipNotes.map((n) => (
              <li key={n.id}>
                <strong>{n.contact}</strong> ({w[CATEGORIES[n.category]]}): {n.text}{" "}
                <span className="muted">— {sourceOf(n, language)}</span>
                {n.expiresOn ? (
                  <>
                    {" "}
                    <span className="muted">{w.memoryKeptUntil(day(`${n.expiresOn}T12:00:00Z`, language))}</span>{" "}
                    <button
                      type="button"
                      className="btn btn-small"
                      disabled={busy}
                      aria-label={w.memoryRenewLabel(n.text)}
                      onClick={() => void attempt(() => api.renewNote(counterparty, n.id), w.memoryRenewed)}
                    >
                      {w.memoryRenew}
                    </button>
                  </>
                ) : null}{" "}
                <Forget what={`"${n.text}"`} busy={busy} onForget={() => forget(n)} />
              </li>
            ))}
          </ul>
          <RelationshipNoteForm
            busy={busy}
            onAdd={(input) =>
              attempt(() => api.addRelationshipNote(counterparty, input), w.memoryRelationshipNoteSaved)
            }
          />
        </>
      ) : null}
    </section>
  );
}

/** Accepts a Suggestion; answers what to tell the person. */
export async function acceptSuggestion(
  api: Api,
  suggestion: Suggestion,
  words: Words = CATALOGUES.en,
): Promise<string> {
  const accepted = await api.acceptSuggestion(suggestion.key);
  return "change" in accepted ? words.memoryAccepted(accepted.change.preview.headline) : words.memoryAcceptedPlain;
}

/** Dismisses a Suggestion; answers what to tell the person. */
export async function dismissSuggestion(
  api: Api,
  suggestion: Suggestion,
  words: Words = CATALOGUES.en,
): Promise<string> {
  await api.dismissSuggestion(suggestion.key);
  return words.memoryDismissed;
}

export function Suggestions({
  suggestions,
  busy,
  onAccept,
  onDismiss,
}: {
  suggestions: readonly Suggestion[];
  busy: boolean;
  onAccept: (s: Suggestion) => void;
  onDismiss: (s: Suggestion) => void;
}) {
  const w = useWords();
  if (suggestions.length === 0) return <p>{w.memoryNoSuggestions}</p>;
  return (
    <ul className="plain-list" aria-label={w.memorySuggestions}>
      {suggestions.map((s) => (
        <li key={s.key} className="card">
          <p>
            <strong>{s.title}</strong>
          </p>
          <p className="muted">{s.why}</p>
          <button
            type="button"
            className="btn btn-go"
            disabled={busy}
            aria-label={w.memoryAcceptLabel(s.title)}
            onClick={() => onAccept(s)}
          >
            {s.proposal === "tree_change" ? w.memoryPreviewChange : w.memoryTakeALook}
          </button>{" "}
          <button
            type="button"
            className="btn"
            disabled={busy}
            aria-label={w.memoryDismissLabel(s.title)}
            onClick={() => onDismiss(s)}
          >
            {w.memoryNotNow}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function Memory({ api }: { api: Api }) {
  const w = useWords();
  const [memory, setMemory] = useState<BusinessMemory | undefined>(undefined);
  const [counterparty, setCounterparty] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.memory().then(setMemory, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api]);
  useEffect(load, [load]);

  const attempt = async (work: () => Promise<string>) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      setNotice(await work());
      load();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (!memory) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.memoryLoading}</p>;
  return (
    <>
      <p className="muted">{w.memoryIntro}</p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <section className="section" aria-labelledby="memory-suggestions">
        <h2 id="memory-suggestions">{w.memorySuggestions}</h2>
        <Suggestions
          suggestions={memory.suggestions}
          busy={busy}
          onAccept={(s) => void attempt(() => acceptSuggestion(api, s, w))}
          onDismiss={(s) => void attempt(() => dismissSuggestion(api, s, w))}
        />
      </section>
      <label className="field" htmlFor="memory-pick">
        {w.memoryCounterparty}
      </label>
      <select
        id="memory-pick"
        value={counterparty ?? ""}
        onChange={(e) => setCounterparty(e.target.value || undefined)}
      >
        <option value="">{w.memoryChooseCounterparty}</option>
        {memory.counterparties.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      {counterparty ? <CounterpartyMemoryPanel api={api} counterparty={counterparty} /> : null}
    </>
  );
}
